import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { useIsElementVisible } from 'af-root/hooks/useIsElementVisible';
import type { OnMoreDataEvent } from 'af-root/hooks/useLoadMore';
import { useLoadMore } from 'af-root/hooks/useLoadMore';
import { useSelection } from 'af-root/hooks/useSelection';
import type { TableFilterSelection } from 'af-root/scenes/Company/Shared/Materials/TableFilters';
import TableFilters from 'af-root/scenes/Company/Shared/Materials/TableFilters';

import type { W_JobMaterial_EditBulk_RM_EditSingle } from 'ab-api/web/jobMaterial/editBulk';
import type { W_JobMaterial_FindGroups_VM_Single } from 'ab-api/web/jobMaterial/findGroups';
import type { W_JobMaterial_FindTable_VM_Single } from 'ab-api/web/jobMaterial/findTable';
import type { W_JobMaterial_FindTable_VM } from 'ab-api/web/jobMaterial/findTable';

import { getToLookupByKey } from '@acceligentllc/shared/utils/array';

import { downloadCSV } from 'af-utils/csv.utils';
import { useToggle } from 'af-utils/react.util';

import { Button } from '@acceligentllc/storybook';

import ConfirmationModal from 'af-components/ConfirmationModal';

import * as JobMaterialActions from 'af-actions/jobMaterial';
import * as JobMaterialCostActions from 'af-actions/jobMaterialCost';

import BulkRateUpdateModal from './BulkRateUpdateModal';
import { toCSVData } from './helpers';
import type { JobMaterialFM, JobMaterialsFormInput, JobMaterialsFormOutput } from './JobMaterialsTableForm';
import JobMaterialsTableForm, { JobMaterialFMSchema, JobMaterialFormSchema, createJobMaterialsForm, jobMaterialVMtoFMInput } from './JobMaterialsTableForm';
import MaterialBulkControls from './MaterialBulkControls';
import MaterialsImportModal from './MaterialsImportModal';
import RateHistoryModal from './RateHistoryModal';
import styles from './styles.module.scss';
import TableLegend from './TableLegend';

interface MaterialForDeleteInfo {
	index: number;
	displayName: string;
	jobMaterialId: number;
}

const DELETE_CONFIRMATION_BODY = (
	<>
		The Material will be removed from Job Materials.
		<br />
		<br />
		<b>If the material is no longer available in Company Resources, it cannot be restored.</b>
	</>
);

const BULK_DELETE_CONFIRMATION_BODY = (
	<>
		The selected Materials will be removed from Job Materials.
		<br />
		<br />
		<b>If the materials are no longer available in Company Resources, they cannot be restored.</b>
	</>
);

function checkHasMoreMaterials(res: W_JobMaterial_FindTable_VM) {
	return res.hasMore;
}

interface OwnProps {
	jobId: number;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const JobMaterialsTable: React.FC<Props> = (props) => {
	const { jobId, bulkEdit, findGroups, findJobMaterials, removeBulk, removeScheduledCostForMaterial } = props;

	const bottomElementRef = React.useRef<HTMLDivElement>(null);

	const [editMode, setEditMode] = React.useState(false);
	const [jobMaterials, setJobMaterials] = React.useState<W_JobMaterial_FindTable_VM_Single[]>([]);
	const [tableFilters, setTableFilters] = React.useState<TableFilterSelection>({ group: null, search: '' });
	const [groups, setGroups] = React.useState<W_JobMaterial_FindGroups_VM_Single[]>([]);
	const [jobMaterialForRateModal, setJobMaterialForRateModal] = React.useState<Nullable<JobMaterialFM>>(null);
	const [showBulkRateEditModal, setShowBulkRateEditModal] = React.useState(false);
	const { value: showBulkDeleteModal, setToFalse: closeBulkDeleteModal, setToTrue: openBulkDeleteModal } = useToggle();
	const [materialForDelete, setMaterialForDelete] = React.useState<Nullable<MaterialForDeleteInfo>>(null);

	const showEmptyOptions = !jobMaterials.length && !tableFilters.search && !tableFilters.group;

	const jobMaterialIds = React.useMemo(() => {
		return jobMaterials.map((material) => material.jobMaterialId);
	}, [jobMaterials]);

	const {
		clearAll: clearSelectedMaterials,
		selected: selectedMaterials,
		toggleItem: toggleSelectedMaterial,
		selectAll: selectAllMaterials,
		selectedCount: selectedMaterialCount,
		totalCount: totalMaterialCount,
	} = useSelection(jobMaterialIds);

	const { value: showImportModal, setToTrue: openImportModal, setToFalse: closeImportModal } = useToggle();

	const isScrolledToTheBottom = useIsElementVisible(bottomElementRef);

	const jobMaterialMap = React.useMemo(() => {
		return jobMaterials.reduce<Record<string, W_JobMaterial_FindTable_VM_Single>>(getToLookupByKey((item) => item.materialId), {});
	}, [jobMaterials]);

	const fetchMaterials = React.useMemo(() => {
		return (page: number) => findJobMaterials(jobId, { group: tableFilters.group, search: tableFilters.search, page });
	}, [findJobMaterials, jobId, tableFilters.group, tableFilters.search]);

	const onMoreMaterials = React.useCallback((event: OnMoreDataEvent<W_JobMaterial_FindTable_VM>) => {
		const { shouldReset, data: { materials: _materials } } = event;
		setJobMaterials((_currentMaterials) => {
			if (shouldReset) {
				return _materials;
			}
			return [..._currentMaterials, ..._materials];
		});
		if (shouldReset) {
			clearSelectedMaterials();
		}
	}, [clearSelectedMaterials]);

	const { load: fetchMoreMaterials, reset: resetMaterials, isLoading } = useLoadMore(
		isScrolledToTheBottom,
		fetchMaterials,
		checkHasMoreMaterials,
		onMoreMaterials
	);

	React.useEffect(() => {
		fetchMoreMaterials();
	}, [fetchMoreMaterials]);

	const loadGroups = React.useCallback(async () => {
		const loaded = await findGroups(jobId);
		setGroups(loaded.groups);
	}, [findGroups, jobId]);

	React.useEffect(() => {
		loadGroups();
	}, [loadGroups]);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const form = useForm<JobMaterialsFormInput, any, JobMaterialsFormOutput>({
		resolver: zodResolver(JobMaterialFormSchema),
		values: createJobMaterialsForm(jobMaterials),
		mode: 'onBlur',
	});

	const materialsFieldArray = useFieldArray({
		control: form.control,
		keyName: 'id',
		name: 'materials',
	});

	const materialsForDeleteFieldArray = useFieldArray({
		control: form.control,
		keyName: 'id',
		name: 'materialsForRemove',
	});

	const handleMaterialCheck = React.useCallback((id: number) => {
		toggleSelectedMaterial(id);
	}, [toggleSelectedMaterial]);

	const saveFormData = React.useCallback(async (data: JobMaterialsFormOutput) => {
		const { materials, materialsForRemove } = data;

		const initialMaterials = jobMaterials.map(jobMaterialVMtoFMInput).map((_mat) => JobMaterialFMSchema.parse(_mat));
		const initialMaterialMap = initialMaterials.reduce<Record<string, JobMaterialFM>>(getToLookupByKey((_mat) => _mat.materialId), {});

		const editedMaterials: W_JobMaterial_EditBulk_RM_EditSingle[] = [];
		for (const material of materials) {
			const { materialId, futureRate, currentRate } = material;
			const rm: W_JobMaterial_EditBulk_RM_EditSingle = {
				materialId: material.materialId,
			};
			const originalMaterial = initialMaterialMap[materialId];
			if (futureRate && originalMaterial.futureRate) {
				if (futureRate.baseCost !== originalMaterial.futureRate.baseCost) {
					rm.baseCost = futureRate.baseCost;
				}
				if (futureRate.shippingMarkup !== originalMaterial.futureRate.shippingMarkup) {
					rm.shippingMarkup = futureRate.shippingMarkup;
				}
				if (futureRate.salesMarkup !== originalMaterial.futureRate.salesMarkup) {
					rm.salesMarkup = futureRate.salesMarkup;
				}
				if (futureRate.tax !== originalMaterial.futureRate.tax) {
					rm.tax = futureRate.tax;
				}
			} else {
				if (currentRate.baseCost !== originalMaterial.currentRate.baseCost) {
					rm.baseCost = currentRate.baseCost;
				}
				if (currentRate.shippingMarkup !== originalMaterial.currentRate.shippingMarkup) {
					rm.shippingMarkup = currentRate.shippingMarkup;
				}
				if (currentRate.salesMarkup !== originalMaterial.currentRate.salesMarkup) {
					rm.salesMarkup = currentRate.salesMarkup;
				}
				if (currentRate.tax !== originalMaterial.currentRate.tax) {
					rm.tax = currentRate.tax;
				}
			}
			if (Object.keys(rm).length > 1) {
				editedMaterials.push(rm);
			}
		}

		await bulkEdit(jobId, { jobMaterials: editedMaterials, jobMaterialIdsForRemove: materialsForRemove.map((_mat) => _mat.jobMaterialId) });
		resetMaterials();
	}, [bulkEdit, jobId, jobMaterials, resetMaterials]);

	const handleSubmit = React.useMemo(() => form.handleSubmit(saveFormData, (error) => console.error(error)), [form, saveFormData]);

	React.useEffect(() => {
		if (!editMode) {
			form.reset();
		}
	}, [editMode, form]);

	const leaveEditMode = React.useCallback(() => {
		setEditMode(false);
	}, []);

	const handleSaveChangesClick = React.useCallback(async () => {
		await handleSubmit();
		leaveEditMode();
		resetMaterials();
	}, [handleSubmit, leaveEditMode, resetMaterials]);

	const handleEditTableClick = React.useCallback(() => {
		setEditMode(true);
		clearSelectedMaterials();
	}, [clearSelectedMaterials]);

	const handleCancelEditsClick = React.useCallback(async () => {
		leaveEditMode();
	}, [leaveEditMode]);

	const openBulkEditRatesModal = React.useCallback(() => {
		setShowBulkRateEditModal(true);
	}, []);

	const closeRateHistoryModal = React.useCallback(() => {
		setJobMaterialForRateModal(null);
	}, []);

	const closeBulkEditRatesModal = React.useCallback(() => {
		setShowBulkRateEditModal(false);
	}, []);

	const closeDeleteModal = React.useCallback(() => {
		setMaterialForDelete(null);
	}, []);

	const deleteMaterial = React.useCallback(async () => {
		if (!materialForDelete) {
			return;
		}
		const { jobMaterialId, index } = materialForDelete;
		materialsFieldArray.remove(index);
		materialsForDeleteFieldArray.append({ jobMaterialId });
	}, [materialForDelete, materialsFieldArray, materialsForDeleteFieldArray]);

	const handleMaterialRemove = React.useCallback((index: number) => {
		const material = jobMaterials[index];
		if (!material) {
			return;
		}
		const { jobMaterialId, displayName } = material;
		setMaterialForDelete({ displayName, jobMaterialId, index });
	}, [jobMaterials]);

	const bulkDeleteMaterials = React.useCallback(async () => {
		const selectedJobMaterialIds = Object.keys(selectedMaterials).map(Number);
		if (!selectedJobMaterialIds.length) {
			return;
		}

		await removeBulk(jobId, { jobMaterialIds: selectedJobMaterialIds });
		resetMaterials();
	}, [jobId, removeBulk, resetMaterials, selectedMaterials]);

	const removeScheduledCost = React.useCallback(async (materialId: number) => {
		const material = jobMaterialMap[materialId];
		if (!material) {
			return;
		}
		const { jobMaterialId, futureRate } = material;
		if (!futureRate) {
			return;
		}
		setJobMaterials((_current) => {
			const next = [..._current];
			const index = _current.findIndex((_material) => _material.materialId === materialId);
			const nextMaterial = { ...next[index], futureRate: null };
			next[index] = nextMaterial;
			return next;
		});
		await removeScheduledCostForMaterial(jobMaterialId);
	}, [jobMaterialMap, removeScheduledCostForMaterial]);

	const handleCSVExportClick = React.useCallback(() => {
		const data = toCSVData(jobMaterials);
		downloadCSV(data, 'job_materials.csv');
	}, [jobMaterials]);

	const selectedJobMaterialIds = React.useMemo(() => Object.keys(selectedMaterials).map(Number), [selectedMaterials]);

	const materialBulkControlsAreVisible = !!selectedJobMaterialIds.length;

	const className = `form-box ${styles.JobMaterialsTable}`;

	const importModalTitle = showEmptyOptions ? 'Import Materials' : 'Manage Materials';

	return (
		<>
			<div className={className}>
				{!showEmptyOptions && (
					<div className={styles.tableHeader}>
						<div className={styles.filters}>
							<TableFilters
								className={styles.filters}
								disabled={editMode}
								filters={tableFilters}
								groups={groups}
								onChange={setTableFilters}
								onLoadRequest={loadGroups}
							/>
						</div>
						<div className={styles.actions}>
							{!editMode ? (
								<>
									<Button
										customStyleClass={styles.action}
										icon="ActionDownload"
										label="CSV Export"
										onClick={handleCSVExportClick}
										style="link"
									/>
									<Button
										customStyleClass={styles.action}
										icon="Materials"
										label="Manage Materials"
										onClick={openImportModal}
										style="link"
									/>
									<Button
										customStyleClass={styles.action}
										label="Edit Table"
										onClick={handleEditTableClick}
										style="primary"
									/>
								</>
							) : (
								<>
									<Button
										customStyleClass={styles.action}
										label="Cancel Edits"
										onClick={handleCancelEditsClick}
										style="secondary"
									/>
									<Button
										customStyleClass={styles.action}
										disabled={!form.formState.isValid}
										label="Save Changes"
										onClick={handleSaveChangesClick}
										style="primary"
									/>
								</>
							)}
						</div>
					</div>
				)}
				{showEmptyOptions ? (
					<div className={styles.importInstructions}>
						<div className={styles.instructions}>
							Import Materials to adjust individual Job Material rates.
						</div>
						<div>
							<Button
								icon="ActionPlusSmall"
								label="Import Materials from Company Resources"
								onClick={openImportModal}
								style="link"
								type="button"
							/>
						</div>
					</div>
				) : (
					<>
						<TableLegend />
						<MaterialBulkControls
							isVisible={materialBulkControlsAreVisible}
							onBulkDelete={openBulkDeleteModal}
							onEditRates={openBulkEditRatesModal}
						/>
						<JobMaterialsTableForm
							clearSelectedMaterials={clearSelectedMaterials}
							fields={materialsFieldArray.fields}
							form={form}
							onMaterialCheck={handleMaterialCheck}
							onRatePreview={setJobMaterialForRateModal}
							onRemove={handleMaterialRemove}
							readonly={!editMode}
							removeScheduledCost={removeScheduledCost}
							selectAllMaterials={selectAllMaterials}
							selectedMaterialCount={selectedMaterialCount}
							selectedMaterials={selectedMaterials}
							totalMaterialCount={totalMaterialCount}
						/>
					</>
				)}
				{isLoading && <div className={styles.loadingText}>Loading...</div>}
				<div className={styles.bottomElement} ref={bottomElementRef} />
			</div>
			<MaterialsImportModal
				close={closeImportModal}
				jobId={jobId}
				onSubmit={resetMaterials}
				showModal={showImportModal}
				title={importModalTitle}
			/>
			<RateHistoryModal close={closeRateHistoryModal} material={jobMaterialForRateModal} />
			<BulkRateUpdateModal
				close={closeBulkEditRatesModal}
				jobId={jobId}
				jobMaterialIds={selectedJobMaterialIds}
				onSubmit={resetMaterials}
				showModal={showBulkRateEditModal}
			/>
			<ConfirmationModal
				body={BULK_DELETE_CONFIRMATION_BODY}
				closeModal={closeBulkDeleteModal}
				confirmAction={bulkDeleteMaterials}
				confirmText="Remove Materials"
				showModal={showBulkDeleteModal}
				size="md"
				title="Remove Selected Materials?"
			/>
			<ConfirmationModal
				body={DELETE_CONFIRMATION_BODY}
				closeModal={closeDeleteModal}
				confirmAction={deleteMaterial}
				confirmText="Remove Material"
				showModal={!!materialForDelete}
				size="md"
				title={`Are you sure you want to remove Material (${materialForDelete?.displayName})?`}
			/>
		</>
	);
};

const mapDispatchToProps = () => {
	return {
		findJobMaterials: JobMaterialActions.findTable,
		findGroups: JobMaterialActions.findGroups,
		bulkEdit: JobMaterialActions.editJobMaterialBulk,
		removeBulk: JobMaterialActions.removeBulk,
		removeScheduledCostForMaterial: JobMaterialCostActions.removeScheduledCostForMaterial,
	};
};

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(JobMaterialsTable);
